// designSystemConstants.ts

// Helper function to lighten color (approximate)
import {PaletteOptions} from "@mui/material/styles/createPalette";

const lightenColor = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
        '#' +
        (
            0x1000000 +
            (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
            (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
            (B < 255 ? (B < 1 ? 0 : B) : 255)
        )
            .toString(16)
            .slice(1)
    );
};

// Helper function to darken color (approximate)
const darkenColor = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = ((num >> 8) & 0x00ff) - amt;
    const B = (num & 0x0000ff) - amt;
    return (
        '#' +
        (
            0x1000000 +
            (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
            (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
            (B < 255 ? (B < 1 ? 0 : B) : 255)
        )
            .toString(16)
            .slice(1)
    );
};

// Helper function to convert hex to RGB string
const hexToRgbString = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '0, 0, 0';
};

// Design system overrides

// Primary
export const COLOR_PRIMARY_MAIN = '#b60065';
export const COLOR_PRIMARY_LIGHT = lightenColor(COLOR_PRIMARY_MAIN, 10);
export const COLOR_PRIMARY_DARK = darkenColor(COLOR_PRIMARY_MAIN, 10);
export const COLOR_PRIMARY_CONTRAST_TEXT = '#fff';
export const COLOR_PRIMARY_MAIN_RGB = hexToRgbString(COLOR_PRIMARY_MAIN);
export const COLOR_PRIMARY_LIGHT_RGB = hexToRgbString(COLOR_PRIMARY_LIGHT);
export const COLOR_PRIMARY_DARK_RGB = hexToRgbString(COLOR_PRIMARY_DARK);

// Secondary
export const COLOR_SECONDARY_MAIN = '#142644';
export const COLOR_SECONDARY_LIGHT = lightenColor(COLOR_SECONDARY_MAIN, 10);
export const COLOR_SECONDARY_DARK = darkenColor(COLOR_SECONDARY_MAIN, 10);
export const COLOR_SECONDARY_CONTRAST_TEXT = '#fff';
export const COLOR_SECONDARY_MAIN_RGB = hexToRgbString(COLOR_SECONDARY_MAIN);
export const COLOR_SECONDARY_LIGHT_RGB = hexToRgbString(COLOR_SECONDARY_LIGHT);
export const COLOR_SECONDARY_DARK_RGB = hexToRgbString(COLOR_SECONDARY_LIGHT);

// Typography - h1
export const FONT_WEIGHT_H1 = 700;
export const FONT_SIZE_H1 = '2rem';
export const FONT_LINE_HEIGHT_H1 = 1.2;
export const FONT_LETTER_SPACING_H1 = '-0.03562em';

// Typography - h2
export const FONT_SIZE_H2 = '2.5rem'; // 40px
export const FONT_WEIGHT_H2 = 700;

// Typography - h3
export const FONT_SIZE_H3 = '1.875rem'; // 30px
export const FONT_WEIGHT_H3 = 700;

// Typography - h4
export const FONT_SIZE_H4 = '1.25rem'; // 20px
export const FONT_WEIGHT_H4 = 700;

// Typography - h5
export const FONT_SIZE_H5 = '1rem'; // 16px
export const FONT_WEIGHT_H5 = 700;

// Typography - h6
export const FONT_SIZE_H6 = '0.875rem'; // 14px

// Typography - body1
export const FONT_SIZE_BODY1 = '14px';
export const FONT_LINE_HEIGHT_BODY1 = 1.8;

// Button
export const FONT_SIZE_BUTTON = '14px';
export const FONT_WEIGHT_BUTTON = 'bold';
export const BORDER_RADIUS_BUTTON = '28px';
export const BORDER_WIDTH_BUTTON = '2px';
export const BORDER_STYLE_BUTTON = 'solid';
export const PADDING_BUTTON = '14px 34px';
export const LINE_HEIGHT_BUTTON = '1.285';
export const MIN_HEIGHT_BUTTON = '51px';
export const MIN_WIDTH_BUTTON = '110px';

// Menu
export const FONT_SIZE_MENU = '16px';

// Text
export const COLOR_TEXT_SECONDARY = '#616161';

export const palette = {
    primary: {
        main: COLOR_PRIMARY_MAIN,
        light: COLOR_PRIMARY_LIGHT,
        dark: COLOR_PRIMARY_DARK,
        contrastText: '#fff',
    },
    secondary: {
        main: COLOR_SECONDARY_MAIN,
        light: COLOR_SECONDARY_LIGHT,
        dark: COLOR_SECONDARY_DARK,
        contrastText: '#fff',
    },
    text: {
        secondary: '#616161',
    },
}